import type { EventCategory } from "@cdt/shared";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

interface DeleteCategoryDialogProps {
  categoryName: string;
  usageCount: number;
  otherCategories: EventCategory[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reassignToId: string | undefined) => void;
  pending: boolean;
}

export function DeleteCategoryDialog({
  categoryName,
  usageCount,
  otherCategories,
  open,
  onOpenChange,
  onConfirm,
  pending,
}: DeleteCategoryDialogProps) {
  const [reassignToId, setReassignToId] = useState<string>("");

  // Reset the selection every time the dialog is (re)opened for a new target.
  useEffect(() => {
    if (!open) setReassignToId("");
  }, [open]);

  const eventsLabel = usageCount === 1 ? "evento" : "eventi";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Elimina categoria in uso</DialogTitle>
          <DialogDescription>
            La categoria &laquo;{categoryName}&raquo; e&apos; assegnata a{" "}
            {usageCount} {eventsLabel}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="reassign-target">
            Trasferisci gli eventi a un&apos;altra categoria (opzionale)
          </Label>
          <select
            id="reassign-target"
            value={reassignToId}
            onChange={(e) => setReassignToId(e.target.value)}
            disabled={pending || otherCategories.length === 0}
            className="h-10 w-full rounded-md border bg-background px-3 text-sm disabled:opacity-50"
          >
            <option value="">Nessuna — lascia senza categoria</option>
            {otherCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            {reassignToId
              ? `${usageCount === 1 ? "L'evento verra' spostato" : "Gli eventi verranno spostati"} nella categoria selezionata.`
              : `${usageCount === 1 ? "L'evento restera'" : "Gli eventi resteranno"} senza categoria e potrai riassegnarli in seguito.`}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Annulla
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(reassignToId || undefined)}
            disabled={pending}
          >
            {pending ? "Eliminazione..." : "Elimina comunque"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
